/**
 * 标准配合冗余约束校验器。
 *
 * 文件职责：
 * 1. 扫描同一实体对上的重复 Distance / Angle 维度约束；
 * 2. 扫描同一实体对上的重复或等价方向约束；
 * 3. 将几何上暂时一致但自由度已被重复约束的 Mate 标记为 overDefined；
 * 4. 只消费 Mate Definition 纯数据，不依赖求解顺序、Viewer 或 UI 状态。
 */

import {
  AssemblyMateType,
  MateAlignment,
  MateSolveStatus,
  type AssemblyMateDefinition,
  type MateEntityRef,
} from '../../definition';
import type { AssemblyMateSolveDiagnostic } from '../StandardMateSolverTypes';

/** 无参考实体的稳定占位。 */
const NO_REFERENCE_ENTITY_KEY = 'reference:none';

/** 方向等价判断使用的角度容差。 */
const DIRECTION_REDUNDANCY_ANGLE_EPSILON = 1e-5;

/** 平行同向的方向约束槽位。 */
const PARALLEL_ALIGNED_DIRECTION_SLOT = 'direction:parallel:aligned';

/** 平行反向的方向约束槽位。 */
const PARALLEL_ANTI_ALIGNED_DIRECTION_SLOT = 'direction:parallel:antiAligned';

/** 垂直方向约束槽位。 */
const PERPENDICULAR_DIRECTION_SLOT = 'direction:perpendicular';

/**
 * 冗余扫描中已登记的基准 Mate。
 */
interface RedundancyRegisteredMate {
  /** Mate ID。 */
  readonly mateId: string;
  /** 创建时间，用于把错误稳定归属到后添加的 Mate。 */
  readonly createdAt: number;
}

/**
 * 冗余约束校验输入。
 */
export interface StandardMateRedundantConstraintValidationInput {
  /** 待校验的 Mate Definition。*/
  readonly mateDefinitions: readonly AssemblyMateDefinition[];
  /**
   * 当前 PropertyManager 尚未提交的临时 Mate ID。
   *
   * 设计说明：
   * 临时 Mate 和已提交 Mate 约束同一自由度时，应阻止确认，但不应把既有装配体链路标记为
   * overDefined；只有已提交 Mate 之间的冗余才代表文档本身已经过定义。
   */
  readonly transientMateIds?: readonly string[];
}

/**
 * 判断是否为当前需要做冗余扫描的维度类 Mate。
 *
 * @param mate Mate Definition。
 * @returns 是否为 Distance / Angle 且未抑制。
 */
function isRedundantDimensionMateCandidate(mate: AssemblyMateDefinition): boolean {
  return !mate.suppressed
    && (mate.mateType === AssemblyMateType.Distance || mate.mateType === AssemblyMateType.Angle);
}

/**
 * 判断是否为当前需要做冗余扫描的方向类 Mate。
 *
 * @param mate Mate Definition。
 * @returns 是否为方向类 Mate 且未抑制。
 */
function isRedundantDirectionalMateCandidate(mate: AssemblyMateDefinition): boolean {
  return !mate.suppressed
    && (
      mate.mateType === AssemblyMateType.Parallel
      || mate.mateType === AssemblyMateType.Perpendicular
      || mate.mateType === AssemblyMateType.Angle
    );
}

/**
 * 解析实体的稳定冗余校验 key。
 *
 * @param entity Mate 实体。
 * @returns 稳定实体 key。
 */
function resolveEntityRedundancyKey(entity: MateEntityRef): string {
  return [
    entity.componentInstanceId,
    entity.stableRef,
    entity.entityKind,
  ].join('|');
}

/**
 * 解析无方向实体对 key。
 *
 * 设计说明：
 * SOLIDWORKS 中同一实体对上的重复维度约束不会因为用户先后选择顺序不同而变成独立自由度。
 * 因此这里对实体 key 排序，避免 A-B 和 B-A 漏检。
 *
 * @param firstEntity 第一实体。
 * @param secondEntity 第二实体。
 * @returns 无方向实体对 key。
 */
function resolveUnorderedEntityPairKey(
  firstEntity: MateEntityRef,
  secondEntity: MateEntityRef,
): string {
  return [
    resolveEntityRedundancyKey(firstEntity),
    resolveEntityRedundancyKey(secondEntity),
  ].sort().join('::');
}

/**
 * 解析维度 Mate 的冗余约束 key。
 *
 * @param mate Mate Definition。
 * @returns 冗余约束 key。
 */
function resolveDimensionMateRedundancyKey(mate: AssemblyMateDefinition): string {
  const pairKey = resolveUnorderedEntityPairKey(mate.firstEntity, mate.secondEntity);
  if (mate.mateType === AssemblyMateType.Distance) {
    /**
     * SOLIDWORKS 中同一实体对上的 Distance Mate 无论当前距离测量条件如何，
     * 都是在约束同一组平移自由度。尤其平面-平面没有圆形距离条件，旧定义中可能没有
     * `distanceMode`，而控制器新建定义会带默认值；因此冗余 key 必须忽略该 UI 条件。
     */
    return [
      mate.mateType,
      pairKey,
    ].join('||');
  }

  return [
    mate.mateType,
    pairKey,
    mate.referenceEntity?.stableRef ?? NO_REFERENCE_ENTITY_KEY,
  ].join('||');
}

/**
 * 将角度归一到 `[0, Math.PI]`。
 *
 * 设计说明：
 * SOLIDWORKS 的标准 Angle Mate 在方向残差上只需要比较两条方向之间的夹角。
 * 对于用户输入或序列化恢复中出现的等价角度，这里统一映射到半周范围，避免漏检
 * `0 / 180` 与 Parallel 的等价关系。
 *
 * @param angle 原始角度。
 * @returns 半周范围内的角度；非法值返回 null。
 */
function normalizeAngleToHalfTurn(angle: number | undefined): number | null {
  if (typeof angle !== 'number' || !Number.isFinite(angle)) {
    return null;
  }

  const fullTurn = Math.PI * 2;
  const normalized = ((angle % fullTurn) + fullTurn) % fullTurn;
  return normalized > Math.PI ? fullTurn - normalized : normalized;
}

/**
 * 判断角度是否在容差内相等。
 *
 * @param first 第一角度。
 * @param second 第二角度。
 * @returns 是否在方向冗余容差内相等。
 */
function areAnglesEquivalent(first: number, second: number): boolean {
  return Math.abs(first - second) <= DIRECTION_REDUNDANCY_ANGLE_EPSILON;
}

/**
 * 解析 Angle Mate 在原始实体方向上的等效夹角。
 *
 * 设计说明：
 * Angle Mate 的 Anti-Aligned 会先反转第一方向再比较角度。因此在判断它是否与
 * Parallel / Perpendicular 等价时，必须还原到“第一实体方向与第二实体方向”的实际夹角。
 *
 * @param mate Angle Mate。
 * @returns 等效夹角；无法解析时返回 null。
 */
function resolveEffectiveAngleDimension(mate: AssemblyMateDefinition): number | null {
  const normalizedAngle = normalizeAngleToHalfTurn(mate.dimension?.value);
  if (normalizedAngle === null) {
    return null;
  }
  return mate.alignment === MateAlignment.AntiAligned
    ? Math.PI - normalizedAngle
    : normalizedAngle;
}

/**
 * 解析方向类 Mate 的自由度槽位。
 *
 * @param mate Mate Definition。
 * @returns 方向自由度槽位；普通非 0/90/180 角度或带参考实体角度返回 null。
 */
function resolveDirectionalConstraintSlot(mate: AssemblyMateDefinition): string | null {
  if (mate.mateType === AssemblyMateType.Parallel) {
    return mate.alignment === MateAlignment.AntiAligned
      ? PARALLEL_ANTI_ALIGNED_DIRECTION_SLOT
      : PARALLEL_ALIGNED_DIRECTION_SLOT;
  }

  if (mate.mateType === AssemblyMateType.Perpendicular) {
    return PERPENDICULAR_DIRECTION_SLOT;
  }

  if (mate.mateType !== AssemblyMateType.Angle || mate.referenceEntity) {
    return null;
  }

  const effectiveAngle = resolveEffectiveAngleDimension(mate);
  if (effectiveAngle === null) {
    return null;
  }
  if (areAnglesEquivalent(effectiveAngle, 0)) {
    return PARALLEL_ALIGNED_DIRECTION_SLOT;
  }
  if (areAnglesEquivalent(effectiveAngle, Math.PI)) {
    return PARALLEL_ANTI_ALIGNED_DIRECTION_SLOT;
  }
  if (areAnglesEquivalent(effectiveAngle, Math.PI / 2)) {
    return PERPENDICULAR_DIRECTION_SLOT;
  }
  return null;
}

/**
 * 解析方向 Mate 的冗余约束 key。
 *
 * @param mate Mate Definition。
 * @returns 冗余约束 key；无法参与方向等价判断时返回 null。
 */
function resolveDirectionalMateRedundancyKey(mate: AssemblyMateDefinition): string | null {
  if (!isRedundantDirectionalMateCandidate(mate)) {
    return null;
  }

  const constraintSlot = resolveDirectionalConstraintSlot(mate);
  if (!constraintSlot) {
    return null;
  }

  return [
    'direction',
    resolveUnorderedEntityPairKey(mate.firstEntity, mate.secondEntity),
    constraintSlot,
  ].join('||');
}

/**
 * 尝试记录一条冗余配合诊断。
 *
 * @param input 冗余诊断输入。
 */
function appendRedundantMateDiagnostic(input: {
  readonly mate: AssemblyMateDefinition;
  readonly key: string;
  readonly firstMateByKey: Map<string, RedundancyRegisteredMate>;
  readonly diagnostics: AssemblyMateSolveDiagnostic[];
  readonly diagnosedMateIds: Set<string>;
  readonly label: string;
  readonly transientMateIds: ReadonlySet<string>;
}): void {
  const firstMate = input.firstMateByKey.get(input.key);
  if (!firstMate) {
    input.firstMateByKey.set(input.key, {
      mateId: input.mate.mateId,
      createdAt: input.mate.createdAt,
    });
    return;
  }

  /**
   * SOLIDWORKS 的错误提示通常让用户处理最近添加或最近编辑的冲突配合。
   * 求解器内部会按组件图排序 Mate，因此这里不能使用遍历顺序决定诊断归属；
   * 必须按文档创建时间选择更早的 Mate 作为基准，让后添加的 Mate 承担 overDefined。
   */
  const redundantMate = input.mate.createdAt < firstMate.createdAt
    ? firstMate
    : {
        mateId: input.mate.mateId,
        createdAt: input.mate.createdAt,
      };
  const referenceMate = input.mate.createdAt < firstMate.createdAt
    ? {
        mateId: input.mate.mateId,
        createdAt: input.mate.createdAt,
      }
    : firstMate;
  const transientMateId = input.transientMateIds.has(input.mate.mateId)
    ? input.mate.mateId
    : input.transientMateIds.has(firstMate.mateId)
      ? firstMate.mateId
      : null;

  if (input.mate.createdAt < firstMate.createdAt) {
    input.firstMateByKey.set(input.key, referenceMate);
  }

  if (transientMateId) {
    if (input.diagnosedMateIds.has(transientMateId)) {
      return;
    }

    input.diagnosedMateIds.add(transientMateId);
    input.diagnostics.push({
      mateId: transientMateId,
      status: MateSolveStatus.Unsolved,
      message: `当前临时${input.label}配合与 ${referenceMate.mateId} 约束同一组实体自由度，请修改配合类型、实体或尺寸后再确认。`,
    });
    return;
  }

  if (input.diagnosedMateIds.has(redundantMate.mateId)) {
    return;
  }

  input.diagnosedMateIds.add(redundantMate.mateId);
  input.diagnostics.push({
    mateId: redundantMate.mateId,
    status: MateSolveStatus.OverDefined,
    message: `该${input.label}配合与 ${referenceMate.mateId} 约束同一组实体自由度，当前装配体配合链路过定义。`,
  });
}

/**
 * 校验重复或等价 Mate 是否造成过定义。
 *
 * @param input 冗余约束校验输入。
 * @returns 冗余 Mate 诊断。
 */
export function validateRedundantMateDefinitions(
  input: StandardMateRedundantConstraintValidationInput,
): readonly AssemblyMateSolveDiagnostic[] {
  const firstDimensionMateByKey = new Map<string, RedundancyRegisteredMate>();
  const firstDirectionMateByKey = new Map<string, RedundancyRegisteredMate>();
  const diagnosedMateIds = new Set<string>();
  const diagnostics: AssemblyMateSolveDiagnostic[] = [];
  const transientMateIds = new Set(input.transientMateIds ?? []);

  input.mateDefinitions.forEach((mate) => {
    if (isRedundantDimensionMateCandidate(mate)) {
      appendRedundantMateDiagnostic({
        mate,
        key: resolveDimensionMateRedundancyKey(mate),
        firstMateByKey: firstDimensionMateByKey,
        diagnostics,
        diagnosedMateIds,
        label: '维度',
        transientMateIds,
      });
    }

    const directionKey = resolveDirectionalMateRedundancyKey(mate);
    if (directionKey) {
      appendRedundantMateDiagnostic({
        mate,
        key: directionKey,
        firstMateByKey: firstDirectionMateByKey,
        diagnostics,
        diagnosedMateIds,
        label: '方向',
        transientMateIds,
      });
    }
  });

  return diagnostics;
}
